import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Shield, Wifi, Car } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const IMAGES = {
  facility: "/volleyball-celebration.jpg",
  volleyball: "/volleyball-huddle.jpg",
  basketball: "/basketball-smile.jpg",
  gymnastics: "/gymnastics-kids.jpg",
};

const facilityFeatures = [
  {
    icon: Shield,
    title: "Güvenli Ortam",
    desc: "Üniversite kampüsü güvenlik standartlarında, kontrollü giriş-çıkış sistemi.",
  },
  {
    icon: MapPin,
    title: "Merkezi Konum",
    desc: "Bostancı'da, toplu taşımaya yakın ve kolay ulaşım imkânı.",
  },
  {
    icon: Wifi,
    title: "Modern Altyapı",
    desc: "Son teknoloji spor ekipmanları ve bakım altı tesisler.",
  },
  {
    icon: Car,
    title: "Otopark",
    desc: "Üniversite kampüsünde yeterli otopark alanı.",
  },
];

const galleryItems = [
  { image: IMAGES.facility, title: "Bostancı Yerleşkesi", span: "md:col-span-2 md:row-span-2" },
  { image: IMAGES.volleyball, title: "Voleybol Salonu", span: "" },
  { image: IMAGES.basketball, title: "Basketbol Sahası", span: "" },
  { image: IMAGES.gymnastics, title: "Cimnastik Salonu", span: "md:col-span-2" },
];

export default function FacilitiesPage() {
  return (
    <div data-testid="facilities-page" className="pt-20">
      <SEOHead
        title="Tesislerimiz - Bostancı Spor Tesisi Ataşehir, Kadıköy"
        description="İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi spor tesisleri. Modern voleybol, basketbol ve cimnastik salonları. Ataşehir, Kadıköy, Maltepe bölgesinde profesyonel spor tesisi."
        keywords="Bostancı spor tesisi, Kadıköy spor salonu, Ataşehir spor tesisi, Maltepe spor salonu, voleybol salonu İstanbul, basketbol salonu Anadolu yakası, cimnastik salonu Kadıköy"
        canonical="/tesisler"
      />
      {/* Hero */}
      <section className="bg-[#1A1A1A] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-gold">Spor Tesisleri</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mt-2">
            Tesislerimiz
          </h1>
          <p className="mt-4 text-base text-white/60 max-w-lg">
            İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi'ndeki modern spor tesislerimiz.
          </p>
        </div>
      </section>

      {/* Gallery Grid (Bento) */}
      <section className="py-24 lg:py-32 bg-white" data-testid="facility-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Galeri</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            Tesis Görüntüleri
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[250px]">
            {galleryItems.map((item) => (
              <div
                key={item.title}
                data-testid={`gallery-${item.title.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative group overflow-hidden ${item.span}`}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="font-heading text-sm font-bold uppercase tracking-wider text-white">
                    {item.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 lg:py-32 bg-surface" data-testid="facility-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Tesis Özellikleri</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            Neden Bostancı Yerleşkesi?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {facilityFeatures.map((feature) => (
              <div key={feature.title} className="p-6 bg-white border border-zinc-200 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300">
                <div className="w-10 h-10 flex items-center justify-center bg-burgundy/5 border border-burgundy/10 mb-4">
                  <feature.icon className="w-4 h-4 text-burgundy" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-base font-bold uppercase tracking-wider text-[#1A1A1A]">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-24 lg:py-32 bg-white" data-testid="facility-map">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Konum</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            Bizi Ziyaret Edin
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="w-full aspect-video bg-gray-100 border border-zinc-200 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3013.06!2d29.0927!3d40.9611!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac79e48bddd6b%3A0x7c96bbcf5e2c8d8c!2sBostanc%C4%B1%2C%20T%C3%BCnel%20Giri%C5%9Fi%20Sk.%20No%3A16%2C%2034744%20Kad%C4%B1k%C3%B6y%2F%C4%B0stanbul!5e0!3m2!1str!2str!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="IRU Spor Kulubu Konum"
                />
              </div>
            </div>
            <div className="p-8 bg-[#1A1A1A] text-white flex flex-col justify-center">
              <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-gold mb-6">
                Adres Bilgileri
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-burgundy-300 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <p className="text-sm text-gray-300">
                    Bostancı, Tünel Girişi Sk. No:16, 34744 Kadıköy/İstanbul
                  </p>
                </div>
                <p className="text-sm text-gray-400 leading-relaxed">
                  İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi. Bostancı metro ve marmaray
                  istasyonuna yürüme mesafesindedir.
                </p>
              </div>
              <Link to="/iletisim" className="mt-8">
                <Button
                  data-testid="facility-contact-btn"
                  className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm w-full"
                >
                  İletişime Geçin <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
